import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import {
  INDIVIDUAL,
  APPROVED,
  CORPORATE,
  INITIATED,
  MY_INFO_RESGISTRATION_URL,
  USER_REGISTRATION_URL,
  NIUM_FOREX_URL,
  SCREEN_NAME_SSN,
  SCREEN_NAME_DOCUMENT_UPLOAD,
  HEADER_FOOTER_IGNORED_URLS,
  US
} from '@Utils/constants';
import { isNewOnboardingEnabledCountry } from '@Utils/library';
import { NF_SCREENS } from '../../screens/nium-forex/root/screenConstants';
import { useToggleFeat } from '../../toggleFeature';
import {
  NewOnboardingFlowReducer,
  NiumForexReducer,
  UserFlowReducer,
  UserSession
} from '../../store.type';
import {
  getUserFlowReducer,
  selectUserStatusReducer,
  getNewUserFlow,
  selectNiumForexReducer,
  selectUserSession
} from '@Redux/selectors';

import { ToggleFeatureState, AdditionalInfoWithSsn } from './types';

const useShowTimeline = (): boolean => {
  const location = useLocation();
  const { pathname } = location;
  const featureState = useToggleFeat() as ToggleFeatureState | undefined;

  const { currentScreen, timeline } = useSelector(getUserFlowReducer) as UserFlowReducer;
  const { account_type, country_code } = useSelector(selectUserSession) as UserSession;
  const {
    status,
    sub_status,
    additional_info,
    account_type: statusAccountType
  } = useSelector(selectUserStatusReducer);
  const { new_onboarding_enabled_users: isNewOnboardingEnabledUser } = useSelector(
    getNewUserFlow
  ) as NewOnboardingFlowReducer;
  const { currentScreen: niumForexCurrentScreen } = useSelector(
    selectNiumForexReducer
  ) as NiumForexReducer;

  // Early return: hidden on signin/signup etc., but only when not in ignored URLs
  if (!HEADER_FOOTER_IGNORED_URLS.includes(pathname)) {
    const isHeaderDisabled = /signin|signup|invite\/|password|logout|feedback\//gi.test(pathname);
    if (isHeaderDisabled || pathname === '/') return false;
  }

  if (!currentScreen) return false;

  const isRecipientScreen = featureState?.toggleFeature && currentScreen === 'add_beneficiary';
  const isTimelineRequired =
    !isRecipientScreen && timeline?.[0]?.label && timeline?.[0]?.timeline_sequence;

  if (!isTimelineRequired) return false;

  // renderTimeLine — new onboarding check
  const newOnBoardingUI = isNewOnboardingEnabledCountry(country_code);
  if (newOnBoardingUI && isNewOnboardingEnabledUser && pathname === USER_REGISTRATION_URL) {
    return false;
  }

  // isUsNewFlow — US doc upload / SSN checks
  const isUsIndividual = account_type === INDIVIDUAL && country_code === US;
  if (isUsIndividual && SCREEN_NAME_DOCUMENT_UPLOAD === currentScreen) return false;
  const additionalInfoWithSsn = additional_info as AdditionalInfoWithSsn | undefined;
  if (
    (additionalInfoWithSsn?.ssn_verification_attempts ?? 0) >= 2 &&
    isUsIndividual &&
    SCREEN_NAME_SSN === currentScreen
  ) {
    return false;
  }

  const isAckScreen =
    pathname === '/acknowledgement/none/awaiting/' ||
    pathname === '/acknowledgement/none/approved/';
  const isUserApproved = status === APPROVED && isAckScreen;
  if (isUserApproved) return false;

  // noTimelineForCorp
  const noTimelineForCorp =
    [APPROVED, INITIATED].includes(status) && isAckScreen && statusAccountType === CORPORATE;
  if (noTimelineForCorp) return false;

  // isREKYC
  const isREKYC = status === INITIATED && sub_status === 'REKYC';
  if (isREKYC) return false;

  // isDashboard
  const isDashboard =
    pathname === '/dashboard' ||
    (pathname === NIUM_FOREX_URL && niumForexCurrentScreen === NF_SCREENS.send_money);
  if (isDashboard) return false;

  // timelineDisabledPaths
  const timelineDisabledPaths = [MY_INFO_RESGISTRATION_URL];
  if (timelineDisabledPaths.includes(pathname)) return false;

  return true;
};

export default useShowTimeline;
